import { Box, Button, CircularProgress, Checkbox, FormControlLabel, FormControl, FormHelperText, TextField, Typography, Select, OutlinedInput, Chip, MenuItem, Paper, useTheme, Popover } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { Formik, Form, ErrorMessage, Field } from 'formik'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { supportedFormats, formatDateTimeForInputField, formatDateTimeForDB } from '../../../utils/exam-utilities'
import * as Yup from 'yup'
import React from 'react'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useExamStore } from '../../../store/exam.store'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import { useUserStore } from '../../../store/user.store'
import BreadcrumbList from '../../../components/common/BreadcrumbList'
import { observer } from 'mobx-react'
import { RefreshOutlined } from '@mui/icons-material'

const HOURS = ["00", "01", "02", "03", "04", "05"]
const MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"]
const attachmentsSupportedFormats = ["pdf", "docx", "doc", "xlsx"]

const EditExamForm = observer( () => {

    const UserStore = useUserStore()
    const ExamStore = useExamStore()

    const [isFetchingBatches, setIsFetchingBatches] = useState( false )
    const [openExamInfo, setOpenExamInfo] = useState( null )
    const [academicYear, setAcademicYear] = useState( "-1" )
    const [isUpdating, setIsUpdating] = useState( false )
    const [isOpenExam, setIsOpenExam] = useState( ExamStore.getExam.exam.exam_duration !== null && ExamStore.getExam.exam.exam_duration !== 'null' )

    const { palette, border } = useTheme()
    const navigate = useNavigate()
    const { id } = useParams()

    const initialValues = {
        title: ExamStore.getExam.exam.exam_name || '',
        description: ExamStore.getExam.exam.exam_description || '',
        topics: ExamStore.getExam.exam.exam_topics || '',
        startDateTime: dayjs( formatDateTimeForInputField( ExamStore.getExam.exam.exam_start_date, ExamStore.getExam.exam.exam_start_time ) ),
        endDateTime: dayjs( formatDateTimeForInputField( ExamStore.getExam.exam.exam_end_date, ExamStore.getExam.exam.exam_end_time ) ),
        selectedBatches: ExamStore.getExam.exam.batches_ids,
        duration: ExamStore.getExam.exam.exam_duration || null,
        durationHours: ExamStore.getExam.exam?.exam_duration?.split( ":" )[0] || "00",
        durationMinutes: ExamStore.getExam.exam?.exam_duration?.split( ":" )[1] || "00",
        maximumAttempt: ExamStore.getExam.exam?.maximum_attempts || 1
    }

    const validationSchema = Yup.object().shape( {
        title: Yup.string().required( "Exam title is required." ).max( 250, "Max length 250 characters" ),
        description: Yup.string().required( "Exam description is required." ).min( 5, "Description as to be least 5 characters." ),
        topics: Yup.array().required( "Please provide the exam topics." ),
        thumbnail: Yup.mixed().nullable().notRequired().test( "FILE_FORMAT", "Unsupported file format, accepted formats: JPG,JPEG,PNG.", ( value ) => {
            if ( value )
                return supportedFormats.includes( value.type )
            return true
        } ),
        // startDateTime: Yup.date().required().min( new Date(), "Date is invalid" ),
        // endDateTime: Yup.date().required().when( "startDateTime", ( startDate ) => {
        //     if ( startDate ) return Yup.date().min( startDate, "End date must be after start date" )
        // } ),
        attachments: Yup.mixed().nullable().notRequired().test( "ATTACHMENTS_FILE_FORMAT", "Unsupported attachment format accepted formats: PDF,DOCX,DOC,XLS.", ( value ) => {
            if ( value ) {
                const files = Object.values( value )
                for ( let i = 0; i < files.length; i++ ) {
                    const file = files[i]
                    if ( !attachmentsSupportedFormats.includes( file.type ) ) {
                        return false
                    }
                }
                return true
            }
            else return true
        } ),
        duration: Yup.string().nullable().test( "CHECK_DURATION", "Invalid exam duration", ( value ) => {
            if ( isOpenExam ) {
                if ( value && value !== '00:00' ) return true
                return false
            } else return true
        } ),
        maximumAttempt: Yup.number().required( "This field is required!" ).min( 1, "At least 1 attempt must be given" )
    } )

    const getBatches = async ( year = academicYear, refresh = true ) => {
        setIsFetchingBatches( true )
        await UserStore.fetchUserBatchesAndSection( year, refresh )
        setIsFetchingBatches( false )
    }

    const refreshYears = async () => {
        await UserStore.fetchUserAcademicYears( true )
    }

    const submit = async ( values, props ) => {
        if ( new Date( formatDateTimeForInputField( ExamStore.getExam?.exam?.exam_start_date, ExamStore.getExam?.exam?.exam_start_time ) ) < new Date() ) {
            toast.error( "Exam already started! can't edit now" )
            navigate( `/faculty/exams/${ExamStore.getExam?.exam?.exam_id}` )
        }
        setIsUpdating( true )
        const formData = new FormData()
        formData.append( 'title', values.title )
        if ( values.thumbnail )
            formData.append( "thumbnail", values.thumbnail, `${values.title} - thumb.${values.thumbnail.name.split( "." )[values.thumbnail.name.split( "." ).length - 1]}` )
        formData.append( 'description', values.description )
        formData.append( 'topics', JSON.stringify( values.topics ) )
        formData.append( 'startDateTime', JSON.stringify( formatDateTimeForDB( values.startDateTime, 'en-IN', '-' ) ) )
        formData.append( 'endDateTime', JSON.stringify( formatDateTimeForDB( values.endDateTime, 'en-IN', '-' ) ) )
        formData.append( 'selectedBatches', JSON.stringify( values.selectedBatches ) )
        formData.append( 'duration', isOpenExam ? values.duration : null )
        formData.append( 'maximumAttempt', values.maximumAttempt )
        if ( values.attachments )
            for ( let i = 0; i < values.attachments.length; i++ ) {
                formData.append( "attachments[]", values.attachments[i], `${values.title}-${Date.now()}${String( i + 1 )}.${values.attachments[i].name.split( "." )[values.attachments[i].name.split( "." ).length - 1]}` )
            }

        await ExamStore.updateExam( id, formData )
        setIsUpdating( false )
        navigate( `/faculty/exams/${id}` )
    }


    return (
        // <></>
        <Formik onSubmit={submit} initialValues={initialValues} validationSchema={validationSchema}>
            {( { values, setFieldValue } ) => (
                <Form method='POST' autoComplete='off' style={{ height: "100%" }} id="edit-form">
                    <FormControl margin="normal" fullWidth>
                        <label htmlFor="title"><Typography gutterBottom variant="subtitle2" color={palette.form.labelColor}> Exam title * </Typography></label>
                        <Field as={TextField}
                            fullWidth name='title'
                            InputProps={{ sx: { fontSize: "14px" } }}
                            size="small"
                            id='title'
                        />
                        <FormHelperText sx={{ margin: "-5px 0 0 2px" }}><span style={{ color: "#d32f2f", fontWeight: "bold", dispaly: "block" }}><ErrorMessage name='title' /></span></FormHelperText>
                    </FormControl>
                    <FormControl margin="normal" fullWidth>
                        <label htmlFor="description"><Typography gutterBottom variant="subtitle2" color={palette.form.labelColor}> Exam description * </Typography></label>
                        <Field as={TextField}
                            multiline
                            rows={4}
                            fullWidth name='description'
                            InputProps={{ sx: { fontSize: "14px" } }}
                            id='description'
                        />
                        <FormHelperText sx={{ margin: "-5px 0 0 2px", color: "#d32f2f", fontWeight: "bold" }}><ErrorMessage name='description' /></FormHelperText>
                    </FormControl>
                    <FormControl margin="normal" fullWidth>
                        <label htmlFor="topics"><Typography gutterBottom variant="subtitle2" color={palette.form.labelColor}> Topics * </Typography></label>
                        <Field as={TextField} onChange={( e ) => { setFieldValue( 'topics', e.target.value.trim() !== "" ? e.target.value.split( "," ) : '' ) }}
                            InputLabelProps={{ sx: { fontSize: "14px !important", } }}
                            name='topics'
                            id="topics"
                            size="small"
                        />
                        <Typography fontSize="12px" color="error"><ErrorMessage name='topics' /></Typography>
                    </FormControl>
                    <FormControl margin='normal' fullWidth>
                        <label htmlFor='academicYears' > <Typography gutterBottom variant="subtitle2" color={palette.form.labelColor}>Academic year *</Typography> </label>
                        {<Select
                            size="small"
                            sx={{ color: academicYear === "-1" && palette.form.placeholder }}
                            value={academicYear} onChange={( e ) => { setAcademicYear( e.target.value ) }} input={<OutlinedInput />}>
                            <MenuItem sx={{ fontSize: "14px" }} value="-1" disabled>{UserStore.getUserAcademicYears.length === 0 ? "No academic years!" : "Select academic year"}</MenuItem>
                            {UserStore.getUserAcademicYears.length > 0 && UserStore.getUserAcademicYears.map( year => (
                                <MenuItem sx={{ fontSize: "14px" }} key={year.ac_year_id} value={year.ac_year_id}>{year.ac_year}</MenuItem>
                            ) )}
                        </Select>}
                        <Typography onClick={refreshYears} sx={{ cursor: "pointer", '&:hover': { textDecoration: "underline" } }} display="flex" gap="5px" alignItems="center" fontSize="14px" color="secondary" fontWeight="500"><RefreshOutlined fontSize='16px' />Refresh academic years</Typography>
                    </FormControl>
                    <FormControl margin='normal' fullWidth>
                        <label htmlFor='selectedBatches' > <Typography gutterBottom variant="subtitle2" color={palette.form.labelColor}>Select the batches or sections</Typography> </label>
                        <Select value={values.selectedBatches} displayEmpty size="small" disabled={isFetchingBatches || academicYear === "-1"} name='selectedBatches' multiple onChange={( e ) => { setFieldValue( 'selectedBatches', typeof e.target.value === 'string' ? e.target.value.split( "," ) : e.target.value ) }} input={<OutlinedInput />}
                            renderValue={( selected ) => {
                                return (
                                    selected.length > 0 ? <Box fontSize="14px" display="flex" gap="10px" flexWrap="wrap" color={selected.length === 0 && palette.form.placeholder} >
                                        {selected.map( batch => {
                                            const access = UserStore.getUsersAllBatchesAndSectionList?.find( i => ( +i.batch_assignment_id === +batch || `${i.section_assignment_id}-${i.section_short_name}` === String( batch ) ) )
                                            let title = '', type = 'Section'
                                            if ( access?.section_assignment_id ) {
                                                title = `${access.ac_year}, ${access.course_branch_short_name ? access.course_branch_short_name?.trim() + "," : ""} ${access?.current_year ? `year: ${access?.current_year},` : ""} ${access.current_sem ? `Sem: ${access.current_sem},` : ""} Sec: ${access.section_short_name}`
                                            } else if ( access?.batch_assignment_id ) {
                                                title = `${access.ac_year}, ${access.course_name_short ? access.course_name_short + "," : ""} ${access?.current_year ? `year: ${access?.current_year},` : ""} ${access.current_sem ? `Sem: ${access.current_sem},` : ""} Batch: ${access.batch_short_name}`
                                                type = "Batch"
                                            } return (
                                                <Chip key={batch} sx={{ height: "50px", border: border[1], borderRadius: "40px" }} label={<Box padding="10px 0" display="inline-flex" flexDirection="column">
                                                    <Typography variant='subtitle2' fontSize="12px"><em><strong>{title}</strong></em></Typography>
                                                    <Typography variant='body2' fontSize="10px"><em>{access.ac_year} - {type}</em></Typography>
                                                </Box>} />
                                            )
                                        } )}
                                    </Box> : <Typography fontSize="14px" color={palette.form.placeholder}>Select batch or section</Typography>
                                )
                            }}>
                            <MenuItem value="-" key="key" disabled sx={{ fontSize: "14px" }} color='textSecondary'> {academicYear === "-" ? "Select academic year to select bacthes or sections" : "Select batch"} </MenuItem>

                            {UserStore.getUsersAllBatchesAndSectionList?.filter( item => item.batch_assignment_id && +item.ac_year_id === +academicYear ).length > 0 && <Typography sx={{ pointerEvents: "none" }} paddingLeft="10px" variant='subtitle2' color="secondary">Batches ({UserStore.getUsersAllBatchesAndSectionList?.filter( item => item.batch_assignment_id && +item.ac_year_id === +academicYear ).length})</Typography>}
                            {UserStore.getUsersAllBatchesAndSectionList?.filter( item => item.batch_assignment_id && +item.ac_year_id === +academicYear )?.map( ( batch, k ) => {
                                return ( <MenuItem sx={{ display: "flex", gap: "10px", alignItems: "center", fontSize: "14px" }} key={batch.batch_assignment_id} value={batch.batch_assignment_id}>
                                    {batch.batch_name + "-" + ( batch.course_name_short || "" ) + ( batch.current_year ? ` Y${batch.current_year}` : `` ) + ( batch.current_sem ? ` S${batch.current_sem}` : `` ) + " " + batch.batch_short_name}
                                </MenuItem> )
                            } )}
                            {UserStore.getUsersAllBatchesAndSectionList?.filter( item => item.section_assignment_id && +item.ac_year_id === +academicYear ).length > 0 && <Typography sx={{ pointerEvents: "none" }} paddingLeft="10px" variant='subtitle2' color="secondary">Sections ({UserStore.getUsersAllBatchesAndSectionList?.filter( item => item.section_assignment_id && +item.ac_year_id === +academicYear ).length})</Typography>}
                            {UserStore.getUsersAllBatchesAndSectionList?.filter( item => item.section_assignment_id && +item.ac_year_id === +academicYear )?.map( ( batch, k ) => {
                                return ( <MenuItem sx={{ display: "flex", gap: "10px", alignItems: "center", fontSize: "14px" }} key={batch.section_assignment_id} value={batch.section_assignment_id + "-" + batch.section_short_name}>
                                    {batch.course_branch_short_name + "-" + ( batch.course_short_name || "" ) + ( batch.current_year ? ` Y${batch.current_year}` : `` ) + ( batch.current_sem ? ` S${batch.current_sem}` : `` ) + " (" + batch.section_short_name + ")"}
                                </MenuItem> )
                            } )}
                            {UserStore.getUsersAllBatchesAndSectionList?.filter( item => item.section_assignment_id && +item.ac_year_id === +academicYear ).length === 0 && UserStore.getUsersAllBatchesAndSectionList?.filter( item => item.batch_assignment_id && item.ac_year_id === +academicYear ).length === 0 && <Typography sx={{ pointerEvents: "none" }} paddingLeft="18px" variant='subtitle2' color="textSecondary">No sections or batches for selected academic year!</Typography>}
                        </Select>
                        {isFetchingBatches && <Typography variant='body2' color="textSecondary"> <CircularProgress size={10} sx={{ marginRight: "5px" }} /> Loading batches...</Typography>}
                        {academicYear === "-1" && <Typography fontSize="14px" gutterBottom variant='body2' color="textSecondary"><em> Select academic year to select batches or sections.</em></Typography>}
                        {( !isFetchingBatches && academicYear !== "-1" ) && <Typography onClick={() => getBatches( academicYear, true )} sx={{ cursor: "pointer", '&:hover': { cursor: academicYear === "-1" && "not-allowed", textDecoration: academicYear !== "-1" && "underline" } }} display="flex" gap="5px" alignItems="center" fontSize="14px" color={academicYear === "-1" ? "GrayText" : "secondary"} fontWeight="500"><RefreshOutlined fontSize='16px' /> Refresh batches</Typography>}

                    </FormControl>

                    <FormControl margin='normal' variant='filled' fullWidth sx={{ width: { lg: "214px", md: "214px", sm: "214px", xs: "100%" } }}>
                        <label htmlFor='maximumAttempt'> <Typography gutterBottom variant="subtitle2" color={palette.form.labelColor}>Maximum attempt *</Typography> </label>
                        <Field as={TextField} size="small" fullWidth name='maximumAttempt' />
                        <Typography fontSize="12px" variant='subtitle2' color="errorMessage"><ErrorMessage name='maximumAttempt' /></Typography>
                    </FormControl>

                    <FormControl margin='normal' sx={{ display: "flex", maxWidth: "250px" }}>
                        <Box>
                            <label htmlFor='startdatetime' > <Typography gutterBottom variant="subtitle2" color={palette.form.labelColor}>Start date and time *</Typography> </label>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    id="startdatetime"
                                    sx={{ width: "100%", fontSize: "14px", '& input': { padding: "10px" } }}
                                    minDateTime={dayjs( new Date() )}
                                    value={dayjs( values.startDateTime )}
                                    format="DD-MM-YYYY hh:mm A"
                                    onChange={( e ) => { setFieldValue( 'startDateTime', e ) }}
                                />
                            </LocalizationProvider>
                        </Box>
                        <Typography variant="subtitle2" fontSize="12px" color="textSecondary">Enter start date and time.</Typography>
                        <Typography color="errorMessage"><ErrorMessage name='startDateTime' /></Typography>
                    </FormControl>

                    <FormControl margin='normal' sx={{ maxWidth: "250px", display: "flex" }}>
                        <Box>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    id="startdatetime"
                                    minDateTime={dayjs( values.startDateTime )}
                                    sx={{ width: "100%", fontSize: "14px", '& input': { padding: "10px" } }}
                                    value={dayjs( values.endDateTime )}
                                    format="DD-MM-YYYY hh:mm A"
                                    onChange={( e ) => { setFieldValue( 'endDateTime', e ) }}
                                // onAccept={( e ) => {  setFieldValue( 'endDateTime', e.$d ) }}
                                // renderInput={( params ) => <TextField InputLabelProps={{ sx: { fontSize: "14px" } }} {...params} sx={{ width: { lg: "214px", md: "214px", sm: "214px", xs: "100%" } }} />}
                                />
                            </LocalizationProvider>
                        </Box>
                        <Typography variant="subtitle2" fontSize="12px" color="textSecondary">Enter end date and time.</Typography>
                        <Typography color="errorMessage"><ErrorMessage name='endDateTime' /></Typography>
                    </FormControl>

                    {isOpenExam && <FormControl variant='outlined' sx={{ display: "flex" }}>
                        <Typography variant='subtitle2' color={palette.form.labelColor} gutterBottom>Exam duration</Typography>
                        <Box display="flex" flexWrap="wrap" gap="10px" alignItems="center">
                            <FormControl sx={{ maxWidth: "250px" }} fullWidth>
                                <Select endAdornment={<Typography fontSize="12px" paddingRight="20px" color="textSecondary" sx={{ pointerEvents: "none" }} >HOURS</Typography>} size="small" onChange={e => { setFieldValue( 'duration', `${e.target.value}:${values.durationMinutes}` ) }} value={values.durationHours}>
                                    {HOURS.map( hour => (
                                        <MenuItem key={hour} value={hour}>{hour}</MenuItem>
                                    ) )}
                                </Select>
                                {/* <TextField type="number" sx={{ width: "246px" }} InputLabelProps={{ sx:{fontSize:"14px !important"} }} label="Hours" onWheel={e => e.target.blur()} onChange={e => { setDurationHours( e.target.value ); setFieldValue( 'duration', `${e.target.value}:${durationMinutes}` ) }} value={durationHours} /> */}
                            </FormControl >
                            <FormControl sx={{ maxWidth: "250px" }} fullWidth>
                                <Select endAdornment={<Typography fontSize="12px" paddingRight="20px" color="textSecondary" sx={{ pointerEvents: "none" }} >MIN</Typography>} size="small" onChange={e => { setFieldValue( 'duration', `${values.durationHours}:${e.target.value}` ) }} value={values.durationMinutes}>
                                    {MINUTES.map( min => (
                                        <MenuItem key={min} value={min}>{min}</MenuItem>
                                    ) )}
                                </Select>
                            </FormControl>
                        </Box>

                        <Typography variant="subtitle2" >Exam duration: {`${values.durationHours ? values.durationHours : 0} hours ${values.durationMinutes ? values.durationMinutes : 0} minutes`}</Typography>
                        <Typography variant="subtitle2" color="errorMessage" ><ErrorMessage name='duration' /></Typography>
                    </FormControl>}


                    {<Box position="relative">
                        {<Popover slotProps={{ paper: { sx: { maxWidth: "300px" } } }} anchorEl={openExamInfo} onClose={e => setOpenExamInfo( null )} open={Boolean( openExamInfo )} >
                            <Typography padding="10px" borderBottom="1px solid #d3d3d3" variant="subtitle2" color="secondary">Stretched Exams</Typography>
                            <Typography padding="10px" textAlign="justify" variant='body2'>In stretched exams the the exam countdown once started by a student, ends at the exam's end date and time as opposed to the duration exam where the countdown ends when the duration set by the faculty ends.</Typography>
                        </Popover>}
                        <Box display="flex" alignItems="center">
                            <FormControlLabel control={<Checkbox color='primaryDark' value={!( isOpenExam )} onChange={e => { setIsOpenExam( !e.target.checked ) }} />} label="Stretch exam from start to end time?" />
                            <InfoIcon sx={{ cursor: "pointer" }} onClick={e => setOpenExamInfo( e.currentTarget )} />
                        </Box>
                    </Box>}


                    <Box display="flex" alignItems="center" marginTop="40px" gap="20px">
                        <Button disableElevation sx={{ marginBottom: "20px", textTransform: "capitalize" }} disabled={( isUpdating )} type='submit'
                            variant='contained' >{isUpdating ? "Updating..." : "Update"}</Button>
                        <Button disableElevation sx={{ marginBottom: "20px", textTransform: "capitalize" }} variant='contained' color="error" onClick={() => navigate( `/faculty/exams/${ExamStore.getExam.exam.exam_id}` )}>Cancel</Button>
                    </Box>
                </Form >
            )}
        </Formik >
    );
}
)
export default function EditExam () {
    // const attachmentsRef = useRef()
    const { id } = useParams()
    const ExamStore = useExamStore()

    // const [previewUrl, setPreviewUrl] = useState( null )
    const [inProgress, setInProgress] = useState( true )

    const navigate = useNavigate()
    const UserStore = useUserStore()

    const { palette, border } = useTheme()

    useEffect( () => {
        const getData = async () => {
            try {
                if ( UserStore.getErrorsStatuses?.academicYearsAndBatches )
                    await UserStore.fetchUsersAllBatchesAndSectionList()

                await ExamStore.fetchExam( id )
                const exam = ExamStore.getExam.exam
                if ( new Date( formatDateTimeForInputField( exam.exam_start_date, exam.exam_start_time ) ) < new Date() ) {
                    toast.error( "Exam already started! can't edit now" )
                    navigate( `/faculty/exams/${exam.exam_id}` )
                } else if ( exam.exam_status === 'Published' ) {
                    toast.error( "Exam already published! can't edit now" )
                    navigate( `/faculty/exams/${exam.exam_id}` )
                }
            } catch ( e ) {
                if ( e.response && e.response.data )
                    if ( e.response.data === "Exam not found!" ) {
                        toast.error( "Exam not found" )
                        navigate( '/faculty/exams' )
                    } else
                        toast.error( e.response.data )
                else if ( e.message )
                    toast.error( e.message )
            } finally {
                setInProgress( false )
            }
        }
        getData()
    }, [id, ExamStore, navigate, UserStore] )

    return (
        // <Layout>
        // </Layout>
        <Box padding="20px" flexGrow={1}>
            <BreadcrumbList items={[
                { label: "Exams", link: '/faculty/exams' },
                { label: ExamStore.getExam.exam ? ExamStore.getExam.exam.exam_name : <CircularProgress size={12} />, link: `/faculty/exams/${id}` },
                "Edit"
            ]} />
            <Paper sx={{ flexGrow: 1, minWidth: "0", overflow: "auto", borderRadius: "10px" }} >

                {!inProgress && <Box>
                    <Box padding="20px" sx={{ background: `linear-gradient(233.69deg, #6A6790 -93.75%, #1F1E2A 145.73%)` }} >
                        <Typography variant='h6' color="white" textTransform="capitalize">{ExamStore.getExam?.exam ? ExamStore.getExam.exam.exam_name : ""}</Typography>
                        <Typography variant='body2' color="white" textTransform="capitalize">Edit exam details</Typography>
                    </Box>
                    <Box margin="20px" bgcolor={palette.contentBg} padding="20px" border={border[1]} borderRadius="5px" >
                        <EditExamForm />
                    </Box>
                </Box >}
                {inProgress && <DataLoadingSpinner waitingMessage="Loading exam..." />}
            </Paper >
        </Box >
    );
}
